import React, { FC, useCallback } from "react";
import { QuickFilters } from "@ldp-web/component-library/ui/molecules/quick-filters";
import { useSimpleTranslation } from "@ldp/simple-i18n";
import { useUpdateFilterQueryParams } from "../../hooks";
import { useAnalyticsContext } from "@ldp/common-contexts";
import { ListingURLParamType } from "@ldp/common-types";
import { useRedirectLink } from "../../utils";
import { useSDMRouterContext } from "@ldp/sdm-contexts";
import { QuickFiltersType } from "./quick-filters.component.interface";

export interface IQuickFilterComponentProps {
  /** quick filter data */
  quickFilters: QuickFiltersType[];
}

export const QuickFiltersComponent: FC<IQuickFilterComponentProps> = ({
  quickFilters,
}) => {
  const i18n = useSimpleTranslation();
  const router = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const updateFilterQueryParams = useUpdateFilterQueryParams();
  const { trackEvent } = useAnalyticsContext();

  /**
   * Function to make filter track call with selected quick filters
   * @param selectedGroupCode selected filter group code
   * @param isSelected boolean to check if it's selected or deselected
   * updates filter groups with selected data and makes analytics call
   * with selected filters
   */
  const trackWithSelectedQuickFilters = (filterType: {
    [key: string]: boolean;
  }) => {
    const quickFiltersData = quickFilters;
    const selectedCode = Object.keys(filterType)[0];
    const isSelected = filterType[selectedCode];
    const updatedQuickFilters = quickFiltersData?.map((quickFilter) => {
      if (selectedCode === quickFilter.filterGroup.code) {
        quickFilter.filterGroup.values
          .map((filter) => {
            filter.selected = isSelected;
          })
          .flat();
      }
      return quickFilter;
    });
    const selectedFilterData = updatedQuickFilters
      ?.map((quickFilter) => {
        return quickFilter.filterGroup.values
          .filter((filter) => filter.selected)
          .map((filter) => ({
            category: quickFilter.filterGroup.code,
            value: filter.name,
          }));
      })
      .flat();
    trackEvent("heliosFilterTracking", {
      eventType: "heliosFilterTracking",
      heliosAnalytics: true,
      heliosSnowplow: true,
      trackFilterData: selectedFilterData,
    });
  };

  /**
   * Callback Function to handle quick filters click
   * updates query parameter and selected page number, makes api call
   */
  const handleQuickFilterSelection = useCallback(
    (filterType: { [key: string]: boolean }) => {
      let selectedCode: string, selectedCodeValue: boolean;
      const filterValue: Record<string, string | string[]> = {};
      let updatedUrl: string;
      trackWithSelectedQuickFilters(filterType);

      quickFilters?.map((quickFilter) => {
        selectedCode = Object.keys(filterType)[0];
        selectedCodeValue = filterType[selectedCode];
        quickFilter.filterGroup.values.map(({ code }) => {
          const filterGroupCode = quickFilter.filterGroup.code;
          if (filterGroupCode === selectedCode) {
            filterValue[selectedCode] = filterValue.hasOwnProperty(selectedCode)
              ? [...filterValue[selectedCode], code]
              : [code];
          }
        });
        filterValue[ListingURLParamType.PAGE] = "1";
        updatedUrl = updateFilterQueryParams(filterValue, selectedCodeValue);
        redirectToPage(updatedUrl);
      });
    },
    [quickFilters, updateFilterQueryParams]
  );

  return (
    <QuickFilters
      moreFiltersLabel={i18n("moreFilters")}
      lessFiltersLabel={i18n("lessFilters")}
      buttons={quickFilters}
      ariaLabelGroup={i18n("a11yQuickFilters")}
      numberOfFilters={{ mobile: 2, desktop: 4 }}
      handleOnChange={handleQuickFilterSelection}
      data-testid="quick-filters"
      hideFilterLabel
    />
  );
};

export default QuickFiltersComponent;
